import type { Address } from "viem"
import type { Config } from "wagmi"

import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { LockVenammQuoteService } from "@/domains/lock-venamm-quotes/lock-venamm-quote-service"
import type { LockOnChainTransport } from "@/domains/lock/lock-on-chain-transport"
import type { ITokenLookup } from "@/domains/tokens/types"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { OrchestratedQuery } from "@/domains/library"

import type { VenammNamespace } from "./types"
import { VenammOrchestrator } from "./venamm-orchestrator"
import { VenammSellOrchestrator } from "./venamm-sell-orchestrator"
import { VenammSellStore } from "./venamm-sell-store"
import { VenammStore } from "./venamm-store"

export type VenammBaseNamespaceFactoryParams = {
  config: Config
  venAmmAddress: Address
  lockOnChainTransport: LockOnChainTransport
  tokenLookup: ITokenLookup
  orchestratedQueryStore: OrchestratedQueryStore
  eventBus: IEventBus
}

export type VenammSellNamespaceFactoryParams = {
  chainId: number
  config: Config
  voterAddress: Address
  veTokenAddress: Address
  venAmmAddress: Address
  wallet: IWalletConnectionStore
  lockOnChainTransport: LockOnChainTransport
  tokenLookup: ITokenLookup
  venammStore: VenammStore
  eventBus: IEventBus
  venammQuoteService: LockVenammQuoteService
}

export const VenammNamespaceFactory = {
  /**
   * Creates the base Venamm namespace (supply tracking)
   * Call this first, then create LockVenammQuotes, then call addSellNamespace
   */
  createBase({
    config,
    venAmmAddress,
    lockOnChainTransport,
    tokenLookup,
    orchestratedQueryStore,
    eventBus,
  }: VenammBaseNamespaceFactoryParams): Omit<VenammNamespace, "Sell"> {
    // Create supply tracking store with initial values
    const venammStore = new VenammStore({
      venAmmAddress,
      availableTokenSupply: 0n,
      availableTokenSupplyValue: 0,
    })

    // Create supply tracking orchestrator
    const venammOrchestrator = new VenammOrchestrator(
      venammStore,
      lockOnChainTransport,
      tokenLookup,
      orchestratedQueryStore,
      eventBus,
    )

    // Create query for polling available token supply (60 seconds = 60,000 ms)
    const loadAvailableTokenSupplyQuery = new OrchestratedQuery<bigint>(
      "Venamm Available Token Supply Sync",
      (query) => venammOrchestrator.onLoadAvailableTokenSupplyTrigger(query),
      60_000, // 60 seconds
      venammOrchestrator.onLoadAvailableTokenSupplyQuerySuccess.bind(venammOrchestrator),
      venammOrchestrator.onLoadAvailableTokenSupplyQueryFailed.bind(venammOrchestrator),
      orchestratedQueryStore,
    )

    // Initialize orchestrator with query
    venammOrchestrator.initialize(loadAvailableTokenSupplyQuery)

    // Start polling immediately
    loadAvailableTokenSupplyQuery.start()

    return {
      Store: venammStore,
      Orchestrator: venammOrchestrator,
    }
  },

  /**
   * Adds the Sell sub-namespace to an existing base namespace
   * Call this after LockVenammQuotes has been created
   */
  addSellNamespace(
    baseNamespace: Omit<VenammNamespace, "Sell">,
    {
      chainId,
      config,
      voterAddress,
      veTokenAddress,
      venAmmAddress,
      wallet,
      lockOnChainTransport,
      tokenLookup,
      venammStore,
      eventBus,
      venammQuoteService,
    }: VenammSellNamespaceFactoryParams,
  ): VenammNamespace {
    // Create sell workflow store
    const venammSellStore = new VenammSellStore()

    // Create sell workflow orchestrator
    const venammSellOrchestrator = new VenammSellOrchestrator(
      chainId,
      venammSellStore,
      config,
      null, // Start with no lock - will be updated when NFT is selected
      eventBus,
      voterAddress,
      veTokenAddress,
      venAmmAddress,
      wallet,
      lockOnChainTransport,
      tokenLookup,
      venammStore,
      venammQuoteService,
    )

    return {
      ...baseNamespace,
      Sell: {
        Store: venammSellStore,
        Orchestrator: venammSellOrchestrator,
      },
    }
  },
}
